import { FormEvent, useEffect, useState } from "react";
import { api } from "../api/client";
import type { PIDTicker, PIDDevice, Vehicle, ListResponse } from "../api/types";

function unwrapVehicles(res: ListResponse<Vehicle> | Vehicle[]): Vehicle[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

export default function PIDTickersPage() {
  const [tickers, setTickers] = useState<PIDTicker[]>([]);
  const [devices, setDevices] = useState<PIDDevice[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [pending, setPending] = useState(false);
  const [textEn, setTextEn] = useState("");
  const [textUr, setTextUr] = useState("");
  const [icon, setIcon] = useState("");
  const [number, setNumber] = useState("");
  const [fare, setFare] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [editId, setEditId] = useState<string | null>(null);
  const [editTextEn, setEditTextEn] = useState("");
  const [editTextUr, setEditTextUr] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editNumber, setEditNumber] = useState("");
  const [editFare, setEditFare] = useState("");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editPending, setEditPending] = useState(false);

  const [actionPending, setActionPending] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [tickerRes, devRes, vehRes] = await Promise.all([
        api.listPIDTickers(),
        api.listPIDDevices(),
        api.listVehicles(),
      ]);
      setTickers(tickerRes ?? []);
      setDevices(devRes.data ?? []);
      setVehicles(unwrapVehicles(vehRes));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tickers");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setPending(true);
    try {
      await api.createPIDTicker({
        scope: "global",
        text_en: textEn.trim(),
        text_ur: textUr.trim(),
        icon: icon.trim() || undefined,
        number: number.trim() || undefined,
        fare: fare.trim() || undefined,
        is_active: isActive,
      });
      setSuccess("Ticker created");
      setTextEn("");
      setTextUr("");
      setIcon("");
      setNumber("");
      setFare("");
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create ticker");
    } finally {
      setPending(false);
    }
  }

  function startEdit(t: PIDTicker) {
    setEditId(t.id);
    setEditTextEn(t.text_en);
    setEditTextUr(t.text_ur);
    setEditIcon(t.icon);
    setEditNumber(t.number);
    setEditFare(t.fare);
    setEditIsActive(t.is_active);
  }

  async function onUpdate(e: FormEvent) {
    e.preventDefault();
    if (!editId) return;
    setError(null);
    setSuccess(null);
    setEditPending(true);
    try {
      const ticker = tickers.find((t) => t.id === editId);
      await api.updatePIDTicker(editId, {
        scope: ticker?.scope,
        device_id: ticker?.device_id ?? "",
        vehicle_id: ticker?.vehicle_id ?? "",
        text_en: editTextEn.trim(),
        text_ur: editTextUr.trim(),
        icon: editIcon.trim(),
        number: editNumber.trim(),
        fare: editFare.trim(),
        is_active: editIsActive,
      });
      setSuccess("Ticker updated");
      setEditId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update ticker");
    } finally {
      setEditPending(false);
    }
  }

  async function onDelete(t: PIDTicker) {
    if (!confirm("Delete this ticker?")) return;
    setError(null);
    setSuccess(null);
    setActionPending(t.id);
    try {
      await api.deletePIDTicker(t.id);
      setSuccess("Ticker deleted");
      if (editId === t.id) setEditId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete ticker");
    } finally {
      setActionPending(null);
    }
  }

  async function onMoveUp(idx: number) {
    if (idx === 0) return;
    const reordered = [...tickers];
    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
    setTickers(reordered);
    try {
      await api.reorderPIDTickers(reordered.map((t) => t.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed");
      await load();
    }
  }

  async function onMoveDown(idx: number) {
    if (idx >= tickers.length - 1) return;
    const reordered = [...tickers];
    [reordered[idx], reordered[idx + 1]] = [reordered[idx + 1], reordered[idx]];
    setTickers(reordered);
    try {
      await api.reorderPIDTickers(reordered.map((t) => t.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed");
      await load();
    }
  }

  function scopeBadge(t: PIDTicker): JSX.Element | null {
    if (t.scope === "device" && t.device_id) {
      const d = devices.find((dev) => dev.id === t.device_id);
      return (
        <span className="users-role-pill info" style={{ fontSize: "0.75rem", marginLeft: 4 }}>
          Device: {d?.device_label || d?.hardware_id || t.device_id.slice(0, 8)}
        </span>
      );
    }
    if (t.scope === "vehicle" && t.vehicle_id) {
      const v = vehicles.find((veh) => veh.id === t.vehicle_id);
      return (
        <span className="users-role-pill warn" style={{ fontSize: "0.75rem", marginLeft: 4 }}>
          Vehicle: {v?.vehicle_id || t.vehicle_id.slice(0, 8)}
        </span>
      );
    }
    return null;
  }

  return (
    <div className="page users-page">
      <div className="users-header">
        <div>
          <h1>PID Tickers</h1>
          <p className="secondary-text">Scrolling information items on PID screens.</p>
        </div>
        <button
          className="btn primary users-add-btn"
          type="button"
          onClick={() => setShowCreate((v) => !v)}
        >
          {showCreate ? "Cancel" : "+ Add ticker"}
        </button>
      </div>

      {success && <div className="banner banner-success">{success}</div>}
      {error && <div className="banner banner-error">{error}</div>}

      {showCreate && (
        <section className="card">
          <h3>Create ticker</h3>
          <form className="form-grid" onSubmit={onCreate}>
            <div className="form-row">
              <label>
                Active
                <select value={isActive ? "yes" : "no"} onChange={(e) => setIsActive(e.target.value === "yes")}>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Text (English)
                <input value={textEn} onChange={(e) => setTextEn(e.target.value)} required />
              </label>
              <label>
                Text (Urdu)
                <input value={textUr} onChange={(e) => setTextUr(e.target.value)} required />
              </label>
            </div>
            <div className="form-row">
              <label>
                Icon (optional)
                <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="e.g. bus" />
              </label>
              <label>
                Number (optional)
                <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="e.g. 101" />
              </label>
              <label>
                Fare (optional)
                <input value={fare} onChange={(e) => setFare(e.target.value)} placeholder="e.g. Rs. 50" />
              </label>
            </div>
            <button className="btn primary" type="submit" disabled={pending}>
              {pending ? "Creating…" : "Create ticker"}
            </button>
          </form>
        </section>
      )}

      {editId && (
        <section className="card">
          <h3>Edit ticker</h3>
          <form className="form-grid" onSubmit={onUpdate}>
            <div className="form-row">
              <label>
                Active
                <select value={editIsActive ? "yes" : "no"} onChange={(e) => setEditIsActive(e.target.value === "yes")}>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Text (English)
                <input value={editTextEn} onChange={(e) => setEditTextEn(e.target.value)} required />
              </label>
              <label>
                Text (Urdu)
                <input value={editTextUr} onChange={(e) => setEditTextUr(e.target.value)} required />
              </label>
            </div>
            <div className="form-row">
              <label>
                Icon
                <input value={editIcon} onChange={(e) => setEditIcon(e.target.value)} />
              </label>
              <label>
                Number
                <input value={editNumber} onChange={(e) => setEditNumber(e.target.value)} />
              </label>
              <label>
                Fare
                <input value={editFare} onChange={(e) => setEditFare(e.target.value)} />
              </label>
            </div>
            <div className="form-row">
              <button className="btn ghost" type="button" onClick={() => setEditId(null)} disabled={editPending}>Cancel</button>
              <button className="btn primary" type="submit" disabled={editPending}>
                {editPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="card users-list-card">
        <div className="users-list-head">
          <h3>
            <span className="users-bar" />
            Tickers
            <span className="users-count-pill">{tickers.length}</span>
          </h3>
        </div>

        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Order</th>
                <th>Text (EN)</th>
                <th>Text (UR)</th>
                <th>Number</th>
                <th>Fare</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && tickers.length === 0 && (
                <tr><td colSpan={7} className="empty">Loading…</td></tr>
              )}
              {!loading && tickers.length === 0 && (
                <tr><td colSpan={7} className="empty">— no tickers —</td></tr>
              )}
              {tickers.map((t, idx) => (
                <tr key={t.id} className={"users-row" + (editId === t.id ? " users-row-selected" : "")}>
                  <td>
                    <div style={{ display: "flex", gap: 2 }}>
                      <button
                        className="btn ghost"
                        type="button"
                        disabled={idx === 0}
                        onClick={() => void onMoveUp(idx)}
                        title="Move up"
                        style={{ padding: "0 4px", minWidth: 0 }}
                      >
                        &#9650;
                      </button>
                      <button
                        className="btn ghost"
                        type="button"
                        disabled={idx === tickers.length - 1}
                        onClick={() => void onMoveDown(idx)}
                        title="Move down"
                        style={{ padding: "0 4px", minWidth: 0 }}
                      >
                        &#9660;
                      </button>
                    </div>
                  </td>
                  <td>
                    {t.text_en || <span className="secondary-text">—</span>}
                    {scopeBadge(t)}
                  </td>
                  <td dir="rtl">{t.text_ur || <span className="secondary-text">—</span>}</td>
                  <td>{t.number || "—"}</td>
                  <td>{t.fare || "—"}</td>
                  <td>
                    <span className={`users-status-pill ${t.is_active ? "ok" : "muted"}`}>
                      {t.is_active ? "YES" : "NO"}
                    </span>
                  </td>
                  <td>
                    <div className="users-row-actions">
                      <button className="btn ghost users-edit-btn" type="button" onClick={() => startEdit(t)}>Edit</button>
                      <button
                        className="btn ghost users-delete-btn"
                        type="button"
                        disabled={actionPending === t.id}
                        onClick={() => void onDelete(t)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
